import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import vocabulary_A1 from '../data/vocabulary_A1';
import vocabulary_A2 from '../data/vocabulary_A2';
import vocabulary_B1 from '../data/vocabulary_B1';
import vocabulary_B2 from '../data/vocabulary_B2';
import extra_vocabulary from '../data/extra_vocabulary';
import { useFailedQuestions } from './FailedQuestionsContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { saveQuizScore, updateStudyStreak } from '../utils/storage';
import BackButton from '../components/BackButton';
import Icon from '../components/Icon';
import './styles/VocabularyTestScreen.css';

const VocabularyTestScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const getAns = (item) => (i18n.language !== 'en' && item[i18n.language]) || item.answer;
  const { failedQuestions, setFailedQuestions } = useFailedQuestions();

  const { totalQuestions,
    selectedCategories } = location.state || {};

  const categoryToVocabulary = {
    A1: vocabulary_A1,
    A2: vocabulary_A2,
    B1: vocabulary_B1,
    B2: vocabulary_B2,
    extras: extra_vocabulary,
    failed: failedQuestions,
  };

  const vocabularyData = selectedCategories.reduce((data, categoryId) => {
    const categoryVocabulary = categoryToVocabulary[categoryId];

    if (categoryVocabulary) {
      data.push(...categoryVocabulary);
    }
    return data;
  }, []);

  const storeFailedQuestions = async (failedQuestions) => {
    try {
      await localStorage.setItem('failedQuestions', JSON.stringify(failedQuestions));
    } catch (error) {
      console.error('Error storing failedQuestions:', error);
    }
  };

  const getFailedQuestions = async () => {
    try {
      const storedData = await localStorage.getItem('failedQuestions');
      if (storedData !== null) {
        return JSON.parse(storedData);
      }
      return [];
    } catch (error) {
      console.error('Error retrieving failedQuestions:', error);
      return [];
    }
  };

  const [failedLoaded, setFailedLoaded] = useState(false);
  const [hasPassed, setHasPassed] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [score, setScore] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [spokenFirst, setSpokenFirst] = useState(false);

  // Reset all state and reload failed questions on every navigation to this screen.
  // location.key is unique per navigate() call so this fires even when the path is the same.
  React.useEffect(() => {
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedOption(null);
    setScore(0);
    setQuizFinished(false);
    setHasPassed(false);
    setSpokenFirst(false);
    setFailedLoaded(false);

    const needsFailed = selectedCategories && selectedCategories.includes('failed');
    if (!needsFailed) {
      setFailedLoaded(true);
    } else {
      getFailedQuestions().then((stored) => {
        setFailedQuestions(stored);
        setFailedLoaded(true);
      });
    }
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOptionPress = (optionIndex) => {
    if (selectedOption !== null) {
      // User has already made a selection, do nothing
      return;
    }
    speakAnswer();
    setSelectedOption(optionIndex);

    if (questions[currentQuestionIndex].correctAnswer === optionIndex) {
      // Correct answer
      setScore(score + 1);
    }

    const currentQuestion = questions[currentQuestionIndex];
    const isAlreadyFailed = failedQuestions.some((failedQuestion) => {
      return failedQuestion.question === currentQuestion.toSpeak;
    });

    if (questions[currentQuestionIndex].correctAnswer !== optionIndex && !isAlreadyFailed) {
      // Incorrect answer, add the current question to failedQuestions
      const failedQuestion = {
      question: questions[currentQuestionIndex].toSpeak,
      answer: questions[currentQuestionIndex].answer
      };
      const newFailedQuestions = [...failedQuestions, failedQuestion];
      setFailedQuestions(newFailedQuestions); // Update state

      // Store newFailedQuestions in AsyncStorage
      storeFailedQuestions(newFailedQuestions);
    }
  };

  const fullPool = [
    ...vocabulary_A1, ...vocabulary_A2, ...vocabulary_B1, ...vocabulary_B2, ...extra_vocabulary,
  ];

  const getRandomOptions = (correctAnswer, count) => {
    const randomOptions = [];
    const used = new Set([correctAnswer]);
    let attempts = 0;
    while (randomOptions.length < count && attempts < 2000) {
      const randomIndex = Math.floor(Math.random() * fullPool.length);
      const answer = getAns(fullPool[randomIndex]);
      if (!used.has(answer)) {
        used.add(answer);
        randomOptions.push(answer);
      }
      attempts++;
    }
    return randomOptions;
  };

  const generateQuestions = (vocabularyData, numberOfQuestions) => {
    const questions = [];
    const usedIndexes = new Set();
    let currentQuestionIndex = 0;
    if (numberOfQuestions > vocabularyData.length) {
      numberOfQuestions = vocabularyData.length;
    }
    while (questions.length < numberOfQuestions) {
      const randomIndex = Math.floor(Math.random() * vocabularyData.length);
      if (!usedIndexes.has(randomIndex)) {
        usedIndexes.add(randomIndex);
        const question = vocabularyData[randomIndex].question;
        const correctAnswer = getAns(vocabularyData[randomIndex]);
        const options = getRandomOptions(correctAnswer, 3);
        options.splice(Math.floor(Math.random() * 4), 0, correctAnswer);
        const questionObj = {
          question: `Question ${currentQuestionIndex + 1}/ ${numberOfQuestions}`,
          toSpeak: question,
          options,
          answer: correctAnswer,
          correctAnswer: options.indexOf(correctAnswer),
        };
        questions.push(questionObj);
        currentQuestionIndex++;
      }
    }
    return questions;
  };

  const speakFirstQuestion = () => {
    if (questions.length > 0 && !spokenFirst) {
      const questionText = questions[0].toSpeak;
      setSpokenFirst(true);
    }
  };

  React.useEffect(() => {
    if (!failedLoaded) return;
    if (questions.length > 0) return;

    const generatedQuestions = generateQuestions(vocabularyData, totalQuestions);
    if (generatedQuestions.length > 0) {
      setQuestions(generatedQuestions);
    }
  }, [failedLoaded, questions.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleRestartButtonPress = () => {
    // Reset quiz state to restart the test
    setCurrentQuestionIndex(0);
    setSelectedOption(null);
    setScore(0);
    setQuizFinished(false);
    setHasPassed(false);
    setSpokenFirst(false);
  };

  const generateNewQuestionSet = () => {
    const newQuestions = generateQuestions(vocabularyData, totalQuestions); 
    setQuestions(newQuestions);
    handleRestartButtonPress();
  };

  const handleNextButtonPress = () => {
    // Check if the quiz is finished
    if (currentQuestionIndex < questions.length - 1) {

      setCurrentQuestionIndex(currentQuestionIndex + 1);
      const questionText = questions[currentQuestionIndex+1].toSpeak;

      setSelectedOption(null); // Clear selected option for the next question
      
    } else {
      // Calculate the passing threshold (70%)
      const passingThreshold = 0.7; // 70%

      // Check if the user has passed
      const userScore = score / questions.length;
      const hasUserPassed = userScore >= passingThreshold;
      // Save quiz score and update streak
      saveQuizScore('Vocabulary', score, questions.length);
      updateStudyStreak();
      // Set quizFinished and hasPassed state
      setQuizFinished(true);
      setHasPassed(hasUserPassed);
    }
  };

  const speakNow = (textToRead) => {
    const synth = window.speechSynthesis;
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(textToRead);
    utterance.lang = "fi-FI";
    synth.speak(utterance);
  };

  const speakQuestion = () => {
      const question = questions[currentQuestionIndex];

  };

  const speakAnswer = () => {
    const question = questions[currentQuestionIndex];
    if (question) {
      }
  };
  const passingThreshold = 0.7; // 70%

  // Check if the user has passed
  const quizFinishedBackgroundColor = hasPassed ? 'var(--color-success)' : 'var(--color-error)';

  return (
    <div className="container">
      <BackButton to="/test-setting-screen" label="Test Settings" />
      {quizFinished ? (
        <div>
          <div
            className={[
              'quizFinishedContainer',
              { backgroundColor: quizFinishedBackgroundColor },
            ].join(' ')}
          >
            <div className="scorePercent">
             <h1>{((score / questions.length) * 100).toFixed(0)}%</h1> 
            </div>
            <div className="quizFinishedText">
              {hasPassed ? 'Congratulations!!' : 'Better luck Next time!'}
            </div>
            <div className={hasPassed ? 'passedText' : 'failedText'}>
              {hasPassed ? 'You Passed!' : 'You Failed!'}
            </div>
            <div className="quizScoreText">
              Your Score: {score}/{questions.length}
            </div>
            
            <div className="passingThresholdText">
              You need {Math.round(passingThreshold * 100)}% to pass
            </div>
          </div>
          <button className="restartButton" onClick={handleRestartButtonPress}>
            Retake Test
          </button>
          <button className="restartButton" onClick={generateNewQuestionSet}>
            New Test
          </button>
        </div>
      ) : (
        <>
          {questions.length === 0 ? (
            <div className="noQuestionsContainer">
              <div className="noQuestionsText">No questions available</div>
            </div>
          ) : (
            <>
              <div className="questionContainer">
                <p className="questionText">
                  {questions.length > 0 &&
                    questions[currentQuestionIndex].question} 
                    </p>
                <p className="questionText">
                    {questions.length > 0 &&
                    questions[currentQuestionIndex].toSpeak}
                 <img
                      src={require('../assets/images/voice-dark.png')}
                      style={{ width: 30, height: 30 , marginLeft: 5 }}
                      className='voiceIcon'
                      alt="Speak" onClick={() => speakNow(questions[currentQuestionIndex].toSpeak)}
                    />
                </p>
              </div>
              <div className="optionsContainer">
                {questions.length > 0 &&
                  questions[currentQuestionIndex].options.map((option, index) => {
                    const isCorrect = selectedOption !== null && index === questions[currentQuestionIndex].correctAnswer;
                    const isWrong   = selectedOption !== null && index === selectedOption && index !== questions[currentQuestionIndex].correctAnswer;
                    return (
                      <button
                        key={index}
                        className={['optionButton', isCorrect ? 'correctOption' : isWrong ? 'wrongOption' : ''].join(' ')}
                        onClick={() => handleOptionPress(index)}
                        disabled={selectedOption !== null}
                        aria-label={`${option}${isCorrect ? ' — correct' : isWrong ? ' — incorrect' : ''}`}
                      >
                        <span className="optionRow">
                          <span className="optionText">{option}</span>
                          {isCorrect && <span className="optionFeedback correctFeedback"><Icon name="check" size={16} /> Correct</span>}
                          {isWrong   && <span className="optionFeedback wrongFeedback"><Icon name="cross" size={16} /> Wrong</span>}
                        </span>
                      </button>
                    );
                  })}
                  <button
                className={[
                  'nextButton',
                  selectedOption === null && 'nextButtonDisabled',
                ].join(' ')}
                onClick={handleNextButtonPress}
                disabled={selectedOption === null}
              >
                Next
              </button>
              </div>
              
            </>
          )}
        </>
      )}
      
    </div>
  );
};

export default VocabularyTestScreen;
